/* ------------------------------------------------------------
 * Vault — gated resource library
 *
 * Cards are rendered by sections/vault-hub.liquid, one per resource
 * block. This file owns filtering, sorting, the modal, the lead form
 * and the download itself. No React, no build step — adding a resource
 * is a theme-editor change.
 *
 * ---- Klaviyo -------------------------------------------------
 *
 * Everything uses Klaviyo's *client* endpoints, which are public by
 * design and take a company id rather than a private key. Two paths,
 * deliberately:
 *
 *   marketing consent ticked -> /client/subscriptions  (subscribes to
 *                               the list; this IS a consent record)
 *   consent NOT ticked       -> /client/profiles       (profile only,
 *                               no marketing subscription)
 *
 * Under POPIA a download is not consent to be marketed to, so the
 * second path exists to capture the lead without subscribing them.
 * Both paths still get the file — declining marketing must not be a
 * reason to withhold the asset.
 *
 * ---- Download ------------------------------------------------
 *
 * The `download` attribute is ignored cross-origin, and the PDFs live
 * on cdn.shopify.com, so a plain <a download> opens the file in the
 * browser's viewer instead of saving it. We fetch to a blob and
 * download that (the CDN sends `access-control-allow-origin: *`, so
 * this is allowed) and fall back to opening the URL if that fails.
 * The success panel always carries a real link as well, so a blocked
 * popup or a failed fetch never leaves the visitor with nothing.
 * ------------------------------------------------------------ */

(function () {
	'use strict';

	var KLAVIYO_REVISION = '2024-10-15';
	var KLAVIYO_BASE = 'https://a.klaviyo.com/client';
	var STORAGE_KEY = 'tdmc-vault-unlocked';

	var FOCUSABLE = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled]):not([type="hidden"])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])'
	].join(',');

	var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

	/* ---------- Storage ----------
	 *
	 * Wrapped because private windows and blocked site data make these
	 * throw rather than return empty.
	 */

	function hasUnlocked(id) {
		try {
			var raw = window.localStorage.getItem(STORAGE_KEY);
			if (!raw) return false;
			if (raw === '1') return true;
			return JSON.parse(raw).indexOf(id) !== -1;
		} catch (e) {
			return false;
		}
	}

	function storeUnlock(id, scope) {
		try {
			if (scope === 'resource') {
				var raw = window.localStorage.getItem(STORAGE_KEY);
				var list = [];
				if (raw && raw !== '1') {
					list = JSON.parse(raw);
				}
				if (list.indexOf(id) === -1) list.push(id);
				window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
			} else {
				window.localStorage.setItem(STORAGE_KEY, '1');
			}
		} catch (e) {
			/* Non-fatal: the visitor just sees the form again next time. */
		}
	}

	/* klaviyo.isIdentified() returns a *Promise*, so the raw value is always
	   truthy and `Boolean(...)` on it silently unlocks every gated resource.
	   It has to be awaited. Resolved once up front and cached on the hub so
	   open() can stay synchronous; if it has not resolved yet the visitor
	   just sees the form, which is the safe direction to fail. */

	function refreshIdentified(hub) {
		try {
			var klaviyo = window.klaviyo;
			if (!klaviyo || !klaviyo.isIdentified) return;

			var result = klaviyo.isIdentified();

			if (result && typeof result.then === 'function') {
				result
					.then(function (value) {
						hub.identified = value === true;
					})
					.catch(function () {});
			} else {
				hub.identified = result === true;
			}
		} catch (e) {
			/* Leave the cached value alone; false is the safe default. */
		}
	}

	/* ---------- Analytics ---------- */

	function track(gaEvent, klaviyoMetric, payload) {
		try {
			if (window.gtag) window.gtag('event', gaEvent, payload);
		} catch (e) {
			/* ignore */
		}
		try {
			if (window.klaviyo && window.klaviyo.track) window.klaviyo.track(klaviyoMetric, payload);
		} catch (e) {
			/* ignore */
		}
	}

	function resourcePayload(data) {
		return {
			resource_id: data.id,
			resource_title: data.title,
			resource_type: data.type,
			resource_solution: data.solution,
			resource_industry: data.industry,
			gated: data.gated
		};
	}

	/* ---------- Klaviyo ---------- */

	function klaviyoPost(path, body, companyId) {
		return window.fetch(KLAVIYO_BASE + '/' + path + '/?company_id=' + encodeURIComponent(companyId), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				revision: KLAVIYO_REVISION
			},
			body: JSON.stringify(body)
		});
	}

	function profileAttributes(values, resource) {
		return {
			email: values.email,
			first_name: values.first_name,
			last_name: values.last_name,
			organization: values.company,
			title: values.job_function,
			location: { country: values.country },
			properties: {
				'Job Function': values.job_function,
				Seniority: values.seniority,
				Industry: values.industry,
				'Marketing Consent': values.marketing_consent,
				'Last Resource Downloaded': resource.title,
				'Last Resource Type': resource.type
			}
		};
	}

	function submitLead(values, resource, config) {
		var attrs = profileAttributes(values, resource);
		var request;

		if (values.marketing_consent) {
			request = klaviyoPost(
				'subscriptions',
				{
					data: {
						type: 'subscription',
						attributes: {
							custom_source: 'Vault: ' + resource.title,
							profile: { data: { type: 'profile', attributes: attrs } }
						},
						relationships: { list: { data: { type: 'list', id: config.listId } } }
					}
				},
				config.companyId
			);
		} else {
			request = klaviyoPost(
				'profiles',
				{ data: { type: 'profile', attributes: attrs } },
				config.companyId
			);
		}

		return request.then(function (response) {
			if (!response.ok) {
				throw new Error('Klaviyo responded ' + response.status);
			}

			/* Best-effort: the download must not depend on the event landing. */
			klaviyoPost(
				'events',
				{
					data: {
						type: 'event',
						attributes: {
							properties: resourcePayload(resource),
							metric: { data: { type: 'metric', attributes: { name: 'Downloaded Resource' } } },
							profile: { data: { type: 'profile', attributes: { email: values.email } } }
						}
					}
				},
				config.companyId
			).catch(function () {});

			try {
				if (window.klaviyo && window.klaviyo.identify) {
					window.klaviyo.identify({ $email: values.email });
				}
			} catch (e) {
				/* ignore */
			}

			return response;
		});
	}

	/* ---------- Download ---------- */

	function triggerDownload(url, filename) {
		return window
			.fetch(url)
			.then(function (response) {
				if (!response.ok) throw new Error('Fetch failed ' + response.status);
				return response.blob();
			})
			.then(function (blob) {
				var objectUrl = window.URL.createObjectURL(blob);
				var link = document.createElement('a');
				link.href = objectUrl;
				link.download = filename || 'download.pdf';
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.setTimeout(function () {
					window.URL.revokeObjectURL(objectUrl);
				}, 20000);
				return true;
			})
			.catch(function () {
				/* The success panel's own link is the fallback the visitor can click. */
				return false;
			});
	}

	/* ---------- Hub ---------- */

	function VaultHub(root) {
		this.root = root;
		this.config = {
			companyId: root.dataset.klaviyoCompany || '',
			listId: root.dataset.klaviyoList || '',
			unlockScope: root.dataset.unlockScope === 'resource' ? 'resource' : 'once'
		};

		this.modal = root.querySelector('[data-vault-modal]');
		this.dialog = this.modal ? this.modal.querySelector('.vault-modal__dialog') : null;
		this.titleEl = root.querySelector('[data-vault-modal-title]');
		this.eyebrowEl = root.querySelector('[data-vault-modal-eyebrow]');
		this.introEl = root.querySelector('[data-vault-modal-intro]');
		this.form = root.querySelector('[data-vault-form]');
		this.success = root.querySelector('[data-vault-success]');
		this.successLink = root.querySelector('[data-vault-success-link]');
		this.successText = root.querySelector('[data-vault-success-text]');
		this.formError = root.querySelector('[data-vault-form-error]');
		this.submitBtn = root.querySelector('[data-vault-submit]');

		this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-vault-card]'));
		this.grid = root.querySelector('[data-vault-grid]');
		this.countEl = root.querySelector('[data-vault-count]');
		this.resetBtn = root.querySelector('[data-vault-reset]');
		this.selects = Array.prototype.slice.call(root.querySelectorAll('[data-vault-filter]'));
		this.sortSelect = root.querySelector('[data-vault-sort]');

		this.deepLink = root.dataset.openOnHash === 'true';
		this.active = null;
		this.lastFocused = null;
		this.identified = false;
		this.order = this.cards.slice();

		this.onKeydown = this.onKeydown.bind(this);
		this.onHashChange = this.onHashChange.bind(this);

		refreshIdentified(this);
		this.buildFilterOptions();
		this.bind();
		this.apply();
		this.restoreFromHash();
	}

	VaultHub.prototype.cardData = function (card) {
		return {
			id: card.dataset.resourceId,
			title: card.dataset.resourceTitle,
			type: card.dataset.resourceType || '',
			solution: card.dataset.resourceSolution || '',
			industry: card.dataset.resourceIndustry || '',
			date: card.dataset.resourceDate || '',
			url: card.dataset.resourceUrl || '',
			filename: card.dataset.resourceFilename || '',
			gated: card.dataset.gated === 'true'
		};
	};

	/* Dropdown options are derived from the cards themselves, so adding a
	   resource with a new type never needs a matching taxonomy setting. */

	VaultHub.prototype.buildFilterOptions = function () {
		var self = this;

		this.selects.forEach(function (select) {
			var key = select.dataset.vaultFilter;
			var seen = [];

			self.cards.forEach(function (card) {
				var value = (card.dataset['resource' + key.charAt(0).toUpperCase() + key.slice(1)] || '').trim();
				if (value && seen.indexOf(value) === -1) seen.push(value);
			});

			seen.sort(function (a, b) {
				return a.localeCompare(b);
			});

			seen.forEach(function (value) {
				var option = document.createElement('option');
				option.value = value;
				option.textContent = value;
				select.appendChild(option);
			});

			/* A dimension no resource uses would be an empty dropdown. */
			if (seen.length === 0) {
				var group = select.closest('.vault-filters__group');
				if (group) group.hidden = true;
			}
		});
	};

	VaultHub.prototype.bind = function () {
		var self = this;

		this.cards.forEach(function (card) {
			var trigger = card.querySelector('[data-vault-open]');
			if (!trigger || trigger.tagName === 'A') return;
			trigger.addEventListener('click', function () {
				self.open(card.dataset.resourceId);
			});
		});

		this.selects.forEach(function (select) {
			select.addEventListener('change', function () {
				self.apply();
			});
		});

		if (this.sortSelect) {
			this.sortSelect.addEventListener('change', function () {
				self.apply();
			});
		}

		if (this.resetBtn) {
			this.resetBtn.addEventListener('click', function () {
				self.selects.forEach(function (select) {
					select.value = '';
				});
				if (self.sortSelect) self.sortSelect.value = 'default';
				self.apply();
			});
		}

		if (this.modal) {
			this.modal.querySelectorAll('[data-vault-close]').forEach(function (el) {
				el.addEventListener('click', function () {
					self.close();
				});
			});
		}

		if (this.form) {
			this.form.addEventListener('submit', function (event) {
				event.preventDefault();
				self.submit();
			});
		}

		if (this.deepLink) {
			window.addEventListener('hashchange', this.onHashChange);
		}
	};

	/* ---------- Filtering and sorting ---------- */

	VaultHub.prototype.apply = function () {
		var self = this;
		var active = {};
		var hasFilter = false;

		this.selects.forEach(function (select) {
			if (select.value) {
				active[select.dataset.vaultFilter] = select.value;
				hasFilter = true;
			}
		});

		var visible = 0;

		this.cards.forEach(function (card) {
			var data = self.cardData(card);
			var match = Object.keys(active).every(function (key) {
				return (data[key] || '') === active[key];
			});
			card.hidden = !match;
			if (match) visible += 1;
		});

		this.root.classList.toggle('vault-hub--no-results', visible === 0);

		if (this.countEl) {
			this.countEl.textContent =
				visible === 1 ? '1 resource' : String(visible) + ' resources';
		}

		if (this.resetBtn) {
			this.resetBtn.hidden = !hasFilter && (!this.sortSelect || this.sortSelect.value === 'default');
		}

		this.sort();
	};

	VaultHub.prototype.sort = function () {
		if (!this.grid || !this.sortSelect) return;

		var self = this;
		var mode = this.sortSelect.value;
		var sorted = this.order.slice();

		if (mode === 'az') {
			sorted.sort(function (a, b) {
				return self.cardData(a).title.localeCompare(self.cardData(b).title);
			});
		} else if (mode === 'recent') {
			sorted.sort(function (a, b) {
				return (self.cardData(b).date || '').localeCompare(self.cardData(a).date || '');
			});
		}

		sorted.forEach(function (card) {
			self.grid.appendChild(card);
		});
	};

	/* ---------- Modal ---------- */

	VaultHub.prototype.open = function (id) {
		if (!this.modal || !id) return;

		var card = this.root.querySelector('[data-vault-card][data-resource-id="' + CSS.escape(id) + '"]');
		if (!card) return;

		var data = this.cardData(card);

		this.lastFocused = document.activeElement;
		this.active = data;

		if (this.titleEl) this.titleEl.textContent = data.title;
		if (this.eyebrowEl) this.eyebrowEl.textContent = data.type;

		this.resetForm();

		/* Re-checked on every open so a visitor identified after page load
		   (newsletter popup, Klaviyo email link) stops seeing the form. */
		refreshIdentified(this);

		var unlocked =
			!data.gated || hasUnlocked(id) || (this.config.unlockScope === 'once' && this.identified === true);

		this.modal.hidden = false;
		this.lockScroll(true);

		// Force a reflow so the enter transition runs from its start state.
		void this.modal.offsetHeight;
		this.modal.classList.add('is-open');

		document.addEventListener('keydown', this.onKeydown);

		track('vault_open', 'Opened Resource', resourcePayload(data));

		if (unlocked) {
			this.showSuccess(data, true);
		} else if (this.form) {
			this.form.hidden = false;
			this.focusFirst();
		}

		if (this.deepLink) {
			this.setHash('resource-' + id);
		}
	};

	VaultHub.prototype.close = function () {
		if (!this.modal || this.modal.hidden) return;

		var self = this;
		var id = this.active ? this.active.id : null;

		this.modal.classList.remove('is-open');
		document.removeEventListener('keydown', this.onKeydown);
		this.lockScroll(false);

		var finish = function () {
			self.modal.hidden = true;
		};

		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			finish();
		} else {
			window.setTimeout(finish, 220);
		}

		if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
			this.lastFocused.focus();
		}

		if (this.deepLink && window.location.hash === '#resource-' + id) {
			this.setHash('');
		}

		this.active = null;
	};

	/* ---------- Form ---------- */

	VaultHub.prototype.resetForm = function () {
		if (this.form) {
			this.form.hidden = true;
			this.form.querySelectorAll('.is-invalid').forEach(function (el) {
				el.classList.remove('is-invalid');
			});
		}
		if (this.success) this.success.classList.remove('is-visible');
		if (this.formError) this.formError.classList.remove('is-visible');
		if (this.submitBtn) {
			this.submitBtn.disabled = false;
			this.submitBtn.classList.remove('is-busy');
		}
	};

	VaultHub.prototype.readValues = function () {
		var form = this.form;
		var get = function (name) {
			var el = form.elements[name];
			if (!el) return '';
			return el.type === 'checkbox' ? el.checked : String(el.value || '').trim();
		};

		return {
			first_name: get('first_name'),
			last_name: get('last_name'),
			email: get('email'),
			country: get('country'),
			industry: get('industry'),
			company: get('company'),
			job_function: get('job_function'),
			seniority: get('seniority'),
			marketing_consent: get('marketing_consent') === true,
			terms: get('terms') === true
		};
	};

	VaultHub.prototype.validate = function (values) {
		var form = this.form;
		var ok = true;

		var flag = function (name, valid) {
			var el = form.elements[name];
			if (!el) return;
			/* .vault-check first: both checkboxes share one .vault-field--full
			   wrapper, so matching .vault-field would light up the optional
			   marketing row as well as the required terms row. */
			var wrap = el.closest('.vault-check') || el.closest('.vault-field');
			if (wrap) wrap.classList.toggle('is-invalid', !valid);
			if (!valid) ok = false;
		};

		flag('first_name', values.first_name.length > 0);
		flag('last_name', values.last_name.length > 0);
		flag('email', EMAIL_RE.test(values.email));
		flag('company', values.company.length > 0);
		flag('country', values.country.length > 0);
		flag('industry', values.industry.length > 0);
		flag('job_function', values.job_function.length > 0);
		flag('seniority', values.seniority.length > 0);
		flag('terms', values.terms === true);

		return ok;
	};

	VaultHub.prototype.submit = function () {
		var self = this;
		var resource = this.active;
		if (!resource || !this.form) return;

		var values = this.readValues();

		if (this.formError) this.formError.classList.remove('is-visible');

		if (!this.validate(values)) {
			var firstInvalid = this.form.querySelector('.is-invalid input, .is-invalid select');
			if (firstInvalid) firstInvalid.focus();
			return;
		}

		if (!this.config.companyId || !this.config.listId) {
			this.fail('This form is not connected yet. Please try again later.');
			return;
		}

		if (this.submitBtn) {
			this.submitBtn.disabled = true;
			this.submitBtn.classList.add('is-busy');
		}

		submitLead(values, resource, this.config)
			.then(function () {
				storeUnlock(resource.id, self.config.unlockScope);
				track('vault_unlock', 'Unlocked Resource', resourcePayload(resource));
				self.showSuccess(resource, false);
			})
			.catch(function () {
				self.fail('Something went wrong on our side. Please try again in a moment.');
			});
	};

	VaultHub.prototype.fail = function (message) {
		if (this.formError) {
			this.formError.textContent = message;
			this.formError.classList.add('is-visible');
		}
		if (this.submitBtn) {
			this.submitBtn.disabled = false;
			this.submitBtn.classList.remove('is-busy');
		}
	};

	VaultHub.prototype.showSuccess = function (resource, returning) {
		if (this.form) this.form.hidden = true;

		if (this.successLink) {
			this.successLink.href = resource.url;
			this.successLink.setAttribute('download', resource.filename || '');
		}

		if (this.successText) {
			this.successText.textContent = returning
				? 'Your download should start automatically. If it does not, use the button below.'
				: 'Thanks. Your download should start automatically. If it does not, use the button below.';
		}

		if (this.success) this.success.classList.add('is-visible');

		track('vault_download', 'Downloaded Resource', resourcePayload(resource));

		triggerDownload(resource.url, resource.filename);

		if (this.successLink) this.successLink.focus();
	};

	/* ---------- Scroll lock ----------
	 *
	 * The theme runs Lenis on <html>, so `body { overflow: hidden }` alone
	 * leaves the page scrolling behind the modal. Stop Lenis as well; the
	 * modal body carries `data-lenis-prevent` so it still scrolls.
	 */

	VaultHub.prototype.lockScroll = function (locked) {
		document.documentElement.classList.toggle('vault-modal-open', locked);
		document.body.classList.toggle('vault-modal-open', locked);

		var lenis = window.lenis;
		if (!lenis) return;

		if (locked && typeof lenis.stop === 'function') {
			lenis.stop();
		} else if (!locked && typeof lenis.start === 'function') {
			lenis.start();
		}
	};

	/* ---------- Focus management ---------- */

	VaultHub.prototype.focusFirst = function () {
		var target = this.form.querySelector(FOCUSABLE) || this.modal.querySelector('.vault-modal__close');
		if (target) target.focus();
	};

	VaultHub.prototype.onKeydown = function (event) {
		if (event.key === 'Escape') {
			event.preventDefault();
			this.close();
			return;
		}

		if (event.key !== 'Tab' || !this.dialog) return;

		var focusable = Array.prototype.slice
			.call(this.dialog.querySelectorAll(FOCUSABLE))
			.filter(function (el) {
				return el.offsetParent !== null || el === document.activeElement;
			});

		if (focusable.length === 0) return;

		var first = focusable[0];
		var last = focusable[focusable.length - 1];

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	};

	/* ---------- Deep links ---------- */

	VaultHub.prototype.setHash = function (hash) {
		var url = window.location.pathname + window.location.search + (hash ? '#' + hash : '');
		window.history.replaceState(null, '', url);
	};

	VaultHub.prototype.restoreFromHash = function () {
		if (!this.deepLink) return;
		var match = /^#resource-(.+)$/.exec(window.location.hash);
		if (match) this.open(decodeURIComponent(match[1]));
	};

	VaultHub.prototype.onHashChange = function () {
		var match = /^#resource-(.+)$/.exec(window.location.hash);
		if (match) {
			var id = decodeURIComponent(match[1]);
			if (!this.active || id !== this.active.id) this.open(id);
		} else if (this.active) {
			this.close();
		}
	};

	/* ---------- Public API ---------- */

	var api = {
		instances: [],

		get: function (index) {
			return this.instances[index || 0];
		},

		open: function (id) {
			var instance = this.instances.find(function (hub) {
				return hub.root.querySelector('[data-vault-card][data-resource-id="' + CSS.escape(id) + '"]');
			});
			if (instance) instance.open(id);
		},

		close: function () {
			this.instances.forEach(function (hub) {
				hub.close();
			});
		},

		/* Testing aid: clears the unlock so the form shows again. */
		relock: function () {
			try {
				window.localStorage.removeItem(STORAGE_KEY);
			} catch (e) {
				/* ignore */
			}
		}
	};

	function init(scope) {
		(scope || document).querySelectorAll('[data-vault-hub]').forEach(function (root) {
			if (root.dataset.vaultInit === 'true') return;
			root.dataset.vaultInit = 'true';
			api.instances.push(new VaultHub(root));
		});
	}

	window.TDMCVault = api;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			init();
		});
	} else {
		init();
	}

	/* Theme editor: re-init when the section is re-rendered. */
	document.addEventListener('shopify:section:load', function (event) {
		api.instances = api.instances.filter(function (hub) {
			return document.contains(hub.root);
		});
		init(event.target);
	});

	document.addEventListener('shopify:section:unload', function () {
		document.documentElement.classList.remove('vault-modal-open');
		document.body.classList.remove('vault-modal-open');
		if (window.lenis && typeof window.lenis.start === 'function') {
			window.lenis.start();
		}
		api.instances = api.instances.filter(function (hub) {
			return document.contains(hub.root);
		});
	});
})();
